<template>
  <Card id="shard-week" class="sm:overflow-clip">
    <div class="flex border-b overflow-x-scroll text-nowrap">
      <div
        @click="currentTabIndex = 0"
        :class="[
          'weekly-forecast-tab-button-base',
          currentTabIndex === 0 ? 'weekly-forecast-tab-button-selected' : 'weekly-forecast-tab-button-unselected'
        ]"
      >
        {{ $t('weeklyForecast.rainCloud') }}
      </div>
      <div
        @click="currentTabIndex = 1"
        :class="[
          'weekly-forecast-tab-button-base',
          currentTabIndex === 1 ? 'weekly-forecast-tab-button-selected' : 'weekly-forecast-tab-button-unselected'
        ]"
      >
        {{ $t('weeklyForecast.temperature') }}
      </div>
      <div
        @click="currentTabIndex = 2"
        :class="[
          'weekly-forecast-tab-button-base',
          currentTabIndex === 2 ? 'weekly-forecast-tab-button-selected' : 'weekly-forecast-tab-button-unselected'
        ]"
      >
        {{ $t('weeklyForecast.wind') }}
      </div>
    </div>

    <div ref="tabsContainer" class="relative overflow-hidden transition-[height] duration-300 ease-out">
      <div
        ref="draggableContainer"
        class="flex items-start touch-pan-y"
        :class="{ 'transition-transform duration-300 ease-out': !isDragging }"
        :style="{ transform: `translateX(${x}px)` }"
      >
        <div class="w-full flex-shrink-0">
          <WeeklyForecastRainCloud
            ref="tab0"
            :daily="daily"
            :timezone="timezone"
            :expandedDayIndex="expandedDayIndex"
            :toggleDay="toggleDay"
          />
        </div>
        <div class="w-full flex-shrink-0">
          <WeeklyForecastTemperature
            ref="tab1"
            :daily="daily"
            :timezone="timezone"
            :expandedDayIndex="expandedDayIndex"
            :toggleDay="toggleDay"
          />
        </div>
        <div class="w-full flex-shrink-0">
          <WeeklyForecastWind
            ref="tab2"
            :daily="daily"
            :timezone="timezone"
            :expandedDayIndex="expandedDayIndex"
            :toggleDay="toggleDay"
          />
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useDrag } from '@vueuse/gesture';
import Card from './Card.vue';
import WeeklyForecastRainCloud from './WeeklyForecastRainCloud.vue';
import WeeklyForecastTemperature from './WeeklyForecastTemperature.vue';
import WeeklyForecastWind from './WeeklyForecastWind.vue';

const props = defineProps({
  daily: Array,
  timezone: String,
});

const currentTabIndex = ref(0);
const expandedDayIndex = ref(null);
const tabsContainer = ref(null);
const draggableContainer = ref(null);
const tab0 = ref(null);
const tab1 = ref(null);
const tab2 = ref(null);

const x = ref(0);
const isDragging = ref(false);

const tabs = [tab0, tab1, tab2];
let resizeObserver = null;

function updateContainerHeight() {
  if (tabsContainer.value && draggableContainer.value) {
    const activeTabWrapper = draggableContainer.value.children[currentTabIndex.value];
    if (activeTabWrapper) {
      tabsContainer.value.style.height = `${activeTabWrapper.offsetHeight}px`;
    }
  }
}

useDrag(
  ({ movement: [mx], dragging }) => {
    isDragging.value = dragging;
    if (!draggableContainer.value) return;
    const containerWidth = tabsContainer.value.clientWidth;

    if (dragging) {
      x.value = -currentTabIndex.value * containerWidth + mx;
    } else {
      if (Math.abs(mx) > containerWidth / 5) {
        currentTabIndex.value = Math.min(
          Math.max(currentTabIndex.value - Math.sign(mx), 0),
          tabs.length - 1
        );
      }
      x.value = -currentTabIndex.value * containerWidth;
    }
  },
  {
    domTarget: draggableContainer,
    axis: 'x',
    filterTaps: true
  }
);

watch(currentTabIndex, (newIndex, oldIndex) => {
  if (!tabsContainer.value) return;
  const containerWidth = tabsContainer.value.clientWidth;
  x.value = -newIndex * containerWidth;

  if (resizeObserver && draggableContainer.value) {
    const oldTabEl = draggableContainer.value.children[oldIndex];
    if (oldTabEl) {
      resizeObserver.unobserve(oldTabEl);
    }
    const newTabEl = draggableContainer.value.children[newIndex];
    if (newTabEl) {
      resizeObserver.observe(newTabEl);
    }
  }
});

function toggleDay(index) {
  if (expandedDayIndex.value === index) {
    expandedDayIndex.value = null;
  } else {
    expandedDayIndex.value = index;
  }
}

function handleResize() {
    if (!tabsContainer.value) return;
    const containerWidth = tabsContainer.value.clientWidth;
    x.value = -currentTabIndex.value * containerWidth;
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
  resizeObserver = new ResizeObserver(updateContainerHeight);

  if (draggableContainer.value) {
    const activeTabEl = draggableContainer.value.children[currentTabIndex.value];
    if (activeTabEl) {
      resizeObserver.observe(activeTabEl);
    }
  }
  nextTick(updateContainerHeight);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
});

</script>

<style scoped>
@reference "../assets/main.css";

.weekly-forecast-tab-button-base {
  @apply py-4 px-4 text-sm font-semibold uppercase;
}

.weekly-forecast-tab-button-selected {
  @apply border-b-2 border-primary text-primary;
}

.weekly-forecast-tab-button-unselected {
  @apply text-secondary hover:text-primary cursor-pointer hover:border-b-2 hover:border-hover;
}
</style>